import React from 'react';
import type { ViewerPartConfig } from '../../../server/admin/viewer-config/types';

interface PartsConfigTabProps {
  value: ViewerPartConfig[];
  onChange: (updated: ViewerPartConfig[]) => void;
}

function generateId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function PartsConfigTab({ value, onChange }: PartsConfigTabProps) {
  const addPart = () => {
    onChange([
      ...value,
      { id: generateId(), name: '', quantity: 1, imageUrl: null, order: value.length },
    ]);
  };

  const updatePart = (index: number, field: keyof ViewerPartConfig, val: string | number | null) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const removePart = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const movePart = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= value.length) return;
    const updated = [...value];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated.map((p, i) => ({ ...p, order: i })));
  };

  return (
    <div>
      <div className="vc-list">
        {value.map((part, index) => (
          <div key={part.id} className="vc-list-item">
            <div className="vc-list-item-header">
              <div className="vc-list-item-drag" title="Reorder">
                <button
                  type="button"
                  onClick={() => movePart(index, -1)}
                  disabled={index === 0}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.125rem', opacity: index === 0 ? 0.3 : 1 }}
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => movePart(index, 1)}
                  disabled={index === value.length - 1}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.125rem', opacity: index === value.length - 1 ? 0.3 : 1 }}
                >
                  ▼
                </button>
              </div>
              <span style={{ flex: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                #{index + 1}
              </span>
              <button
                type="button"
                className="vc-list-item-delete"
                onClick={() => removePart(index)}
                title="Remove part"
              >
                ✕
              </button>
            </div>
            <div className="vc-list-item-fields">
              <input
                className="vc-input"
                type="text"
                value={part.name}
                onChange={(e) => updatePart(index, 'name', e.target.value)}
                placeholder="Part name (required)"
              />
              <div className="vc-list-item-row">
                <input
                  className="vc-input"
                  type="number"
                  min={1}
                  value={part.quantity}
                  onChange={(e) => updatePart(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ width: '5rem' }}
                  placeholder="Qty"
                />
                <input
                  className="vc-input"
                  type="text"
                  value={part.code || ''}
                  onChange={(e) => updatePart(index, 'code', e.target.value)}
                  placeholder="Part code (optional)"
                  style={{ flex: 1 }}
                />
              </div>
              <input
                className="vc-input"
                type="url"
                value={part.imageUrl || ''}
                onChange={(e) => updatePart(index, 'imageUrl', e.target.value || null)}
                placeholder="Image URL (optional)"
              />
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="vc-add-btn" onClick={addPart}>
        + Add Part
      </button>
    </div>
  );
}
